import { type ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import type { ChatRailTokens } from "../../tokens";
import { JsonResultRenderer } from "./JsonResultRenderer";
import { PersonRenderer } from "./PersonRenderer";
import { OrganizationRenderer } from "./OrganizationRenderer";
import { DealRenderer } from "./DealRenderer";
import { PartnerRenderer } from "./PartnerRenderer";

type Kind = "invocation" | "result";

interface SkillRenderer {
  match: (skillName: string) => boolean;
  render: (kind: Kind, payload: unknown, T: ChatRailTokens) => ReactNode;
}

const renderers: SkillRenderer[] = [
  {
    match: (n) => n.startsWith("manna.persons."),
    render: (kind, payload, T) => <PersonRenderer kind={kind} payload={payload} T={T} />,
  },
  {
    match: (n) => n.startsWith("manna.organizations."),
    render: (kind, payload, T) => <OrganizationRenderer kind={kind} payload={payload} T={T} />,
  },
  {
    match: (n) => n.startsWith("manna.deals."),
    render: (kind, payload, T) => <DealRenderer kind={kind} payload={payload} T={T} />,
  },
  {
    match: (n) => n.startsWith("manna.partners."),
    render: (kind, payload, T) => <PartnerRenderer kind={kind} payload={payload} T={T} />,
  },
];

export function renderSkillResult(
  skillName: string,
  kind: Kind,
  payload: unknown,
  T: ChatRailTokens,
): ReactNode {
  if (payload == null) {
    return (
      <Box sx={{ color: T.textMute, fontSize: T.fs_meta, fontFamily: T.mono }}>(no payload)</Box>
    );
  }
  for (const r of renderers) {
    if (r.match(skillName)) return r.render(kind, payload, T);
  }
  return <JsonResultRenderer payload={payload} T={T} />;
}

export function ScalarRow({
  label,
  value,
  T,
}: {
  label: string;
  value: ReactNode;
  T: ChatRailTokens;
}) {
  return (
    <Box sx={{ display: "flex", gap: 1, fontFamily: T.sans, fontSize: T.fs_label }}>
      <Typography sx={{ color: T.textMute, fontSize: T.fs_label, minWidth: 80 }}>
        {label}
      </Typography>
      <Typography sx={{ color: T.text, fontSize: T.fs_label, flex: 1 }}>{value}</Typography>
    </Box>
  );
}

export function pickStringField(payload: unknown, ...keys: string[]): string | null {
  if (!payload || typeof payload !== "object") return null;
  const obj = payload as Record<string, unknown>;
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v.length > 0) return v;
  }
  return null;
}
